import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  EventEmitter,
  inject,
  Injectable,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef,
  ViewRef,
} from '@angular/core';
import { Event, NavigationEnd, Router } from '@angular/router';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { Subscription, tap } from 'rxjs';
import { safelyDetectChanges, unsubscribeAll } from 'src/app/common/app.helpers';
import { NotificationType } from '../component-type.enum';
import { NotificationAction } from '../interfaces';
import { NotificationContext } from '../notification.context';
import { EventNotificationsService, NotificationViewModel } from '../services/event-notifications.service';
import { NotificationItem } from '../strategy/event-notifications.interface';

@Component({
  selector: 'app-notifications-dropdown',
  templateUrl: './notifications-dropdown.component.html',
  styleUrls: ['./notifications-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
@Injectable()
export class NotificationsDropdownComponent implements OnInit, OnDestroy {
  @ViewChild('dropdown', { static: true }) public dropdown: NgbDropdown;
  @ViewChild('viewContainer', { read: ViewContainerRef, static: true }) public viewContainer: ViewContainerRef;

  @Output() public dropdownClosed: EventEmitter<void> = new EventEmitter<void>();

  public notificationEngineConnected = false;

  private subscriptions$: Subscription[] = [];
  private notificationItems: NotificationItem[] = [];
  private informationalNotificationsIds: string[] = [];

  private eventNotificationService = inject(EventNotificationsService);
  private changeDetector = inject(ChangeDetectorRef);
  private router = inject(Router);

  public ngOnInit(): void {
    this.subscribeNotifications();
    this.subscribeOnNotificationMessageRemoved();
    this.subscribeRouterChanges();
    this.subscribeOnNotificationChannelCleared();
  }

  public ngOnDestroy(): void {
    unsubscribeAll(this.subscriptions$);
  }

  public get notificationsNumber(): number {
    return this.viewContainer.length;
  }

  public get informationalNotificationsNumber(): number {
    return this.informationalNotificationsIds.length;
  }

  public onDismissAll(): void {
    this.filterInformationNotifications(this.notificationItems).forEach(
      (notificationsFilteredItem: NotificationItem) => {
        this.eventNotificationService.deleteNotification({
          message_sid: notificationsFilteredItem.payload.messageSid,
        } as NotificationAction);
      },
    );
  }

  private filterInformationNotifications(notificationsDeleteItems: NotificationItem[]): NotificationItem[] {
    return notificationsDeleteItems.filter(
      (notificationsDeleteItem: NotificationItem) => !notificationsDeleteItem.payload.actions,
    );
  }

  private subscribeOnNotificationChannelCleared(): void {
    this.subscriptions$.push(
      this.eventNotificationService.notificationChannelCleared.subscribe(() => {
        this.viewContainer.clear();
        this.informationalNotificationsIds = [];
        this.notificationItems = [];
        safelyDetectChanges(this.changeDetector);
      }),
    );
  }

  private subscribeRouterChanges(): void {
    this.subscriptions$.push(
      this.router.events.subscribe((event: Event) => {
        if (this.dropdown.isOpen() && event instanceof NavigationEnd) {
          this.dropdown.close();
          safelyDetectChanges(this.changeDetector);
        }
      }),
    );
  }

  private subscribeNotifications(): void {
    this.subscriptions$.push(
      this.eventNotificationService.notificationChannelConnectionStateChanged.subscribe((connected: boolean) => {
        this.notificationEngineConnected = connected;
        safelyDetectChanges(this.changeDetector);
      }),
    );
    this.subscriptions$.push(
      this.eventNotificationService.notificationChannelInitialMessagesLoaded.subscribe(
        (notificationViewModels: NotificationViewModel[]) => {
          notificationViewModels.forEach((notificationViewModel: NotificationViewModel) =>
            this.addNotification(this.createNotificationComponentView(notificationViewModel)),
          );
        },
      ),
    );

    this.subscriptions$.push(
      this.eventNotificationService.notificationMessageAdded.subscribe(
        (notificationViewModel: NotificationViewModel) => {
          this.addNotification(this.createNotificationComponentView(notificationViewModel));
        },
      ),
    );
  }

  private subscribeOnNotificationMessageRemoved(): void {
    this.subscriptions$.push(
      this.eventNotificationService.notificationMessageRemoved.subscribe((sid: string) =>
        this.removeNotificationMessage(sid),
      ),
    );
  }

  private addNotification(componentRef: ComponentRef<NotificationContext>): void {
    const viewRef: ViewRef = componentRef.hostView;

    if (componentRef.instance.notificationType === NotificationType.Informational) {
      this.informationalNotificationsIds.push(componentRef.instance.data.messageSid);
    }

    this.notificationItems.push({
      viewRef: componentRef.hostView,
      payload: componentRef.instance.data,
    });
    this.viewContainer.insert(componentRef.hostView, 0);
    this.subscribeToNotificationAction(componentRef, viewRef);
    safelyDetectChanges(this.changeDetector);
  }

  private subscribeToNotificationAction(componentRef: ComponentRef<NotificationContext>, viewRef: ViewRef): void {
    this.subscriptions$.push(
      componentRef.instance.actionExecuted
        .pipe(
          tap((notificationAction: NotificationAction) => {
            this.removeNotificationItem(viewRef, this.findNotificationItem(componentRef.instance.data.messageSid));
            // eslint-disable-next-line
            this.eventNotificationService[notificationAction.type](notificationAction);
          }),
        )
        .subscribe(),
    );
  }

  private findNotificationItem(sid: string): NotificationItem {
    return this.notificationItems.find((item: NotificationItem) => item.payload.messageSid === sid);
  }

  private removeNotificationMessage(sid: string): void {
    const item: NotificationItem = this.findNotificationItem(sid);
    if (item) {
      this.removeNotificationItem(item.viewRef, item);
    }
  }

  private removeNotificationItem(viewRef: ViewRef, item: NotificationItem): void {
    const viewIndex: number = this.viewContainer.indexOf(viewRef);

    if (viewIndex !== -1) {
      this.viewContainer.remove(viewIndex);

      this.notificationItems = [...this.notificationItems].filter(
        (notificationsDeleteItem: NotificationItem) =>
          notificationsDeleteItem.payload.messageSid !== item.payload.messageSid,
      );
    }

    this.informationalNotificationsIds = this.informationalNotificationsIds.filter(
      (notificationSid: string) => notificationSid !== item.payload.messageSid,
    );

    if (!this.viewContainer.length) {
      this.dropdown.close();
    }
    safelyDetectChanges(this.changeDetector);
  }

  private createNotificationComponentView(viewModel: NotificationViewModel): ComponentRef<NotificationContext> {
    const componentRef: ComponentRef<NotificationContext> = this.viewContainer.createComponent(viewModel.componentType);
    componentRef.instance.data = viewModel.notificationDataPayload;
    return componentRef;
  }
}
